// Reusable Autocomplete component
import { useState, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'

export type AutocompleteOption = {
  id: string
  label: string
}

type Props = {
  value: string
  onChange: (id: string, label: string) => void
  fetchOptions: (search: string) => Promise<AutocompleteOption[]>
  placeholder?: string
  label?: string
  required?: boolean
  error?: string
  minChars?: number
  queryKey: string
  disabled?: boolean
  initialLabel?: string
}

export function Autocomplete({
  value,
  onChange,
  fetchOptions,
  placeholder = 'Type to search...',
  label,
  required,
  error,
  minChars = 2,
  queryKey,
  disabled,
  initialLabel,
}: Props) {
  const [inputValue, setInputValue] = useState(initialLabel || '')
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Sync input value when initialLabel changes (e.g., when editing)
  useEffect(() => {
    if (initialLabel) {
      setInputValue(initialLabel)
    }
  }, [initialLabel])

  const { data: options = [], isLoading } = useQuery({
    queryKey: [queryKey, 'search', inputValue],
    queryFn: () => fetchOptions(inputValue),
    enabled: inputValue.length >= minChars && isOpen,
    staleTime: 30000,
  })

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue)
    setIsOpen(true)
    setHighlightedIndex(-1)
    // Clear the selected value when user starts typing something different
    if (value && newValue !== initialLabel) {
      onChange('', '')
    }
  }

  const handleSelect = (option: AutocompleteOption) => {
    setInputValue(option.label)
    onChange(option.id, option.label)
    setIsOpen(false)
    setHighlightedIndex(-1)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || options.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex((prev) => (prev < options.length - 1 ? prev + 1 : prev))
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev))
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0 && options[highlightedIndex]) {
          handleSelect(options[highlightedIndex])
        }
        break
      case 'Escape':
        setIsOpen(false)
        setHighlightedIndex(-1)
        break
    }
  }

  const showDropdown = isOpen && inputValue.length >= minChars

  return (
    <div ref={wrapperRef} className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => inputValue.length >= minChars && setIsOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm disabled:bg-gray-100"
      />
      {showDropdown && (
        <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
          {isLoading ? (
            <li className="relative cursor-default select-none px-4 py-2 text-gray-500">
              Loading...
            </li>
          ) : options.length === 0 ? (
            <li className="relative cursor-default select-none px-4 py-2 text-gray-500">
              No results found
            </li>
          ) : (
            options.map((option, index) => (
              <li
                key={option.id}
                onClick={() => handleSelect(option)}
                className={`relative cursor-pointer select-none px-4 py-2 ${
                  highlightedIndex === index
                    ? 'bg-brand-600 text-white'
                    : 'text-gray-900 hover:bg-gray-100'
                }`}
              >
                {option.label}
              </li>
            ))
          )}
        </ul>
      )}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  )
}
